import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { mergeApi, errMsg } from "../institutionMerge.api";

const { Text, Paragraph } = Typography;

/**
 * Détacher un établissement — pull one source back out of the merge by itself, keeping
 * all its data and users, while the others stay merged. Same gate as a full restore,
 * scoped to that source; records created on the target since are untouched.
 */
export function MergeDetach({ merge, onDone }) {
  const [sourceId, setSourceId] = useState(null);
  const [rp, setRp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const detached = new Set((merge.detached || []).map(x => String(x.sourceId)));
  const sources = (merge.sourceInstitutionIds || []).filter(
    s => !detached.has(String(s._id)),
  );
  const chosen = sources.find(s => String(s._id) === String(sourceId));

  const analyse = async id => {
    setSourceId(id);
    setRp(null);
    setOverrides([]);
    setConfirm("");
    if (!id) return;
    setLoading(true);
    try {
      setRp(await mergeApi.detachPreview(merge._id, id));
    } catch (e) {
      notification.error({
        message: "Analyse impossible",
        description: errMsg(e),
      });
    } finally {
      setLoading(false);
    }
  };
  const run = async () => {
    setLoading(true);
    try {
      const r = await mergeApi.detach(merge._id, sourceId, {
        overrides,
        reason: reason.trim() || undefined,
      });
      notification.success({
        message: "Établissement détaché",
        description: `${r.reverted} enregistrement(s) remis sur ${chosen.name}`,
        duration: 8,
      });
      // the source is no longer detachable: clear the choice before the merge reloads
      setSourceId(null);
      setRp(null);
      setOverrides([]);
      setConfirm("");
      setReason("");
      onDone();
    } catch (e) {
      notification.error({
        message: "Détachement refusé",
        description: errMsg(e),
        duration: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const unacked = rp
    ? rp.entangled.warnings.filter(w => !overrides.includes(w.documentId))
    : [];
  const ready =
    rp &&
    rp.canDetach &&
    unacked.length === 0 &&
    chosen &&
    confirm.trim() === chosen.name.trim();

  // detaching the last source would be a full restore under another name — refused
  if (sources.length <= 1)
    return (
      <Alert
        type="info"
        showIcon
        message="Il ne reste qu'un établissement : utilisez la restauration complète"
      />
    );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Détacher un seul établissement — pour une sélection faite par erreur."
        description="Ses prestations, factures, adresses, utilisateurs et responsables reviennent chez lui et il est désarchivé ; les autres établissements restent fusionnés. Les enregistrements créés sur la nouvelle institution depuis la fusion ne bougent pas : ils ne lui ont jamais appartenu."
      />
      <Space wrap>
        <Text strong>Établissement à détacher</Text>
        <Select
          style={{ width: 360 }}
          placeholder="Choisir…"
          value={sourceId}
          onChange={analyse}
          options={sources.map(s => ({ value: s._id, label: s.name }))}
          allowClear
        />
      </Space>
      {loading && !rp && <Text type="secondary">Analyse en cours…</Text>}
      {rp && (
        <>
          <Space size="large" wrap>
            <Statistic title="Reviennent chez lui" value={rp.revertible} />
            <Statistic
              title="Modifiés depuis (laissés)"
              value={rp.driftTotal}
              valueStyle={{ color: rp.driftTotal ? "#d48806" : undefined }}
            />
            <Statistic title="Listes à réajuster" value={rp.arrays} />
            <Statistic
              title="À cheval (bloquants)"
              value={rp.entangled.blocking.length}
              valueStyle={{
                color: rp.entangled.blocking.length ? "#cf1322" : undefined,
              }}
            />
            <Statistic
              title="À cheval (avertissements)"
              value={rp.entangled.warnings.length}
            />
          </Space>
          <Table
            size="small"
            pagination={false}
            rowKey="k"
            dataSource={Object.entries(rp.byField).map(([k, n]) => ({ k, n }))}
            columns={[
              { title: "Relation", dataIndex: "k" },
              { title: "Enregistrements", dataIndex: "n", align: "right" },
            ]}
          />
          {rp.losers.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={`${rp.losers.length} compte(s) en double fusionné(s) depuis cet établissement seront rétablis (désarchivés, e-mail d'origine, rattachements rendus).`}
            />
          )}
          {rp.survivorsLeaving.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Comptes fusionnés qui repartent avec cet établissement en gardant l'historique d'un autre site"
              description={rp.survivorsLeaving.join(", ")}
            />
          )}
          {rp.entangled.blocking.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="Bloqué."
              description="Une facture brouillon non exportée mêle des prestations de cet établissement à d'autres. Annulez-la et réémettez-la dans l'application, puis relancez l'analyse."
            />
          )}
          {(rp.entangled.blocking.length > 0 ||
            rp.entangled.warnings.length > 0) && (
            <Table
              size="small"
              pagination={false}
              rowKey="documentId"
              dataSource={[...rp.entangled.blocking, ...rp.entangled.warnings]}
              columns={[
                { title: "Collection", dataIndex: "model" },
                { title: "N°", dataIndex: "label" },
                {
                  title: "Statut",
                  dataIndex: "status",
                  render: (s, r) => (
                    <Space>
                      {s || "—"}
                      {r.exported && <Tag>exportée</Tag>}
                    </Space>
                  ),
                },
                {
                  title: "De cet établissement / autres",
                  render: (_, r) => `${r.mine} / ${r.others}`,
                },
                {
                  title: "Gravité",
                  dataIndex: "severity",
                  render: s =>
                    s === "blocking" ? (
                      <Tag color="red">bloquant</Tag>
                    ) : (
                      <Tag color="orange">avertissement</Tag>
                    ),
                },
                {
                  title: "Acquitter",
                  render: (_, r) =>
                    r.severity === "warning" ? (
                      <Checkbox
                        checked={overrides.includes(r.documentId)}
                        onChange={e =>
                          setOverrides(o =>
                            e.target.checked
                              ? [...o, r.documentId]
                              : o.filter(x => x !== r.documentId),
                          )
                        }
                      >
                        je prends la responsabilité
                      </Checkbox>
                    ) : null,
                },
              ]}
            />
          )}
          <div>
            <Paragraph style={{ marginBottom: 4 }}>
              <Text strong>Raison du détachement</Text>{" "}
              <Text type="secondary">
                (facultatif — conservée dans l'historique)
              </Text>
            </Paragraph>
            <Input.TextArea
              rows={2}
              style={{ maxWidth: 640, marginBottom: 16 }}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="ex. établissement sélectionné par erreur, demande du client…"
            />
            <Paragraph>
              Pour confirmer, saisissez le nom de l'établissement :{" "}
              <Text code>{chosen && chosen.name}</Text>
            </Paragraph>
            <Space>
              <Input
                style={{ width: 360 }}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
              />
              <Button
                danger
                type="primary"
                disabled={!ready}
                loading={loading}
                onClick={() =>
                  Modal.confirm({
                    title: `Détacher ${chosen.name} ?`,
                    okType: "danger",
                    okText: "Détacher",
                    content: `${rp.revertible} enregistrement(s) reviennent chez ${chosen.name}, qui est désarchivé. Les autres établissements restent fusionnés.`,
                    onOk: run,
                  })
                }
              >
                Détacher
              </Button>
            </Space>
          </div>
        </>
      )}
    </Space>
  );
}
